import { Entity, objectWithoutKeys } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import {
  MarkSubscriptionAsSubscribedAction,
  MarkSubscriptionAsUnsubscribedAction,
} from '../../domains/realtimeSubscriptions/actions.types';
import { Channel } from '../../realtime/types';

import { RealtimeSubscriptionType, RealtimeSubscriptions } from './types';

export type RealtimeSubscriptionsAction = MarkSubscriptionAsSubscribedAction | MarkSubscriptionAsUnsubscribedAction;

export const initialState: RealtimeSubscriptions = {
  AskMeAnythingSummary: {},
  Feed: {},
  GroupEventsStatuses: {},
  GroupUnreadCount: {},
  InboxUnreadCount: {},
  MessageReactions: {},
  ModeratorFeedRemoveThreads: {},
  NestedReplies: {},
  NetworkEventsStatuses: {},
  NotificationsUnreadCount: {},
  PausedFeed: {},
  PausedFeedTelemetry: {},
  PremiumEventRole: {},
  PremiumEventSettings: {},
  PushNotification: {},
  RepliesOnStaticallySortedFeed: {},
  RepliesOnStaticallySortedFeedTelemetry: {},
  RestrictedPostingUpdates: {},
  TeamsMeetingRole: {},
  TeamsMeetingSettings: {},
  TeamsMeetingThreadAnswerState: {},
  TeamsMeetingThreadQueueState: {},
  Thread: {},
  TypingIndicator: {},
  UserActions: {},
  UserEventsStatuses: {},
};

const markAsSubscribed = (
  state: RealtimeSubscriptions,
  subscriptionType: RealtimeSubscriptionType,
  entityId: Entity['id'],
  channel: Channel
) => {
  const subscription = state[subscriptionType][entityId];
  const currentCount = subscription?.count || 0;
  const updatedSubscription = {
    channel,
    count: currentCount + 1,
  };

  return {
    ...state,
    [subscriptionType]: {
      ...state[subscriptionType],
      [entityId]: updatedSubscription,
    },
  };
};

const markAsUnsubscribed = (
  state: RealtimeSubscriptions,
  subscriptionType: RealtimeSubscriptionType,
  entityId: Entity['id'],
  channel: Channel
) => {
  const subscription = state[subscriptionType][entityId];
  if (!subscription) {
    return state;
  }

  const newCount = subscription.count - 1;
  if (newCount < 1) {
    return {
      ...state,
      [subscriptionType]: objectWithoutKeys(state[subscriptionType], [entityId]),
    };
  }

  const updatedSubscription = {
    ...subscription,
    channel,
    count: newCount,
  };

  return {
    ...state,
    [subscriptionType]: {
      ...state[subscriptionType],
      [entityId]: updatedSubscription,
    },
  };
};

const reducer: Reducer<RealtimeSubscriptions, RealtimeSubscriptionsAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'REALTIME_SUBSCRIPTION.MARK_AS_SUBSCRIBED':
      return markAsSubscribed(state, action.payload.subscriptionType, action.payload.entityId, action.payload.channel);
    case 'REALTIME_SUBSCRIPTION.MARK_AS_UNSUBSCRIBED':
      return markAsUnsubscribed(
        state,
        action.payload.subscriptionType,
        action.payload.entityId,
        action.payload.channel
      );
    default:
      return state;
  }
};

export default reducer;
